import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, CloseIcon, DotDotDotVerticalIcon, EditIcon, PlusIcon, ScrollableContainer, TrashCanIcon, } from '@streamr/tv-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { SceneService } from '../services/scene/SceneService';
import { StoredMediaService } from '../services/storedmedia/StoredMediaService';
import { StreamrAppServiceOutput } from '../services/streamrapp/StreamrAppServiceOutput';
import { TextStyle } from '../utils/styled';
export function ScenesPanel({ onClose }) {
    const scenes = SceneService.getOutput().useScenes();
    const liveSceneId = SceneService.getOutput().useLiveSceneId();
    const displaySceneId = SceneService.getOutput().useDisplaySceneId();
    const ready = StreamrAppServiceOutput.getReactiveOutput().useReady();
    const uploadedImages = StoredMediaService.getOutput().useImages();
    const thumbnails = SceneService.getOutput().useThumbnails();
    const restoredRef = useRef(false);
    useEffect(() => {
        SceneService.getInstance();
    }, []);
    useEffect(() => {
        if (restoredRef.current || scenes.length === 0)
            return;
        restoredRef.current = true;
        void StoredMediaService.getInstance().restoreImagesForScenes(scenes);
    }, [scenes]);
    const [openMenu, setOpenMenu] = useState(undefined);
    const closeMenu = useCallback(() => {
        setOpenMenu(undefined);
    }, []);
    const canvasKeys = SceneService.getOutput().useCanvasKeys();
    const canvasRefs = useRef(new Map());
    const readyRef = useRef(ready);
    const liveSceneIdRef = useRef(liveSceneId);
    readyRef.current = ready;
    liveSceneIdRef.current = liveSceneId;
    const setCanvasRef = useCallback((sceneId, canvas) => {
        if (canvas) {
            canvasRefs.current.set(sceneId, canvas);
            if (readyRef.current && sceneId === liveSceneIdRef.current) {
                SceneService.getNotificationInterface().notifyLiveSceneCanvasMounted(sceneId, canvas);
            }
        }
        else {
            canvasRefs.current.delete(sceneId);
        }
    }, []);
    // biome-ignore lint/correctness/useExhaustiveDependencies: canvasKeys triggers re-run after canvas re-key
    useEffect(() => {
        if (!ready || !liveSceneId)
            return;
        const canvas = canvasRefs.current.get(liveSceneId);
        if (canvas) {
            SceneService.getNotificationInterface().notifyLiveSceneCanvasMounted(liveSceneId, canvas);
        }
        const mountedSceneId = liveSceneId;
        return () => {
            SceneService.getNotificationInterface().notifyLiveSceneCanvasUnmounted(mountedSceneId);
        };
    }, [liveSceneId, ready, canvasKeys]);
    return (_jsx(ScenesPanelRoot, { children: _jsx(Content, { className: "tv-ui-shiny-border", children: _jsx(ScrollableContainer, { children: _jsxs(Inner, { children: [_jsxs(Header, { children: [_jsx(Title, { children: "Scenes" }), onClose && (_jsx(HeaderButton, { kind: "pill", onClick: () => onClose(), children: _jsx(CloseIcon, {}) }))] }), _jsx(SceneList, { children: scenes.map((scene) => {
                                const isLive = scene.id === liveSceneId;
                                const isDisplayScene = scene.id === displaySceneId;
                                return (_jsxs(SceneCard, { "$isDisplayScene": isDisplayScene, onClick: () => {
                                        SceneService.getNotificationInterface().notifySetDisplayScene(scene.id);
                                    }, children: [_jsxs(ThumbnailWrapper, { children: [_jsx(SceneThumbnail, { "$bg": scene.backgroundColor, "$bgImageUrl": scene.backgroundSourceId
                                                        ? uploadedImages.find((i) => i.id ===
                                                            scene.backgroundSourceId)?.objectUrl
                                                        : undefined }), isLive ? (_jsx(SceneCanvas, { ref: (el) => setCanvasRef(scene.id, el) }, `canvas-${scene.id}-${canvasKeys[scene.id] || 0}`)) : (thumbnails[scene.id] && (_jsx(ThumbnailImage, { src: thumbnails[scene.id], alt: scene.name }))), _jsx(LiveBadge, { "$isLive": isLive, onClick: (e) => {
                                                        e.stopPropagation();
                                                        SceneService.getNotificationInterface().notifySetLiveScene(scene.id);
                                                    }, children: isLive ? ('LIVE') : (_jsxs(_Fragment, { children: ["GO", _jsx("br", {}), "LIVE"] })) })] }), _jsxs(CardFooter, { children: [_jsx(SceneLabel, { children: scene.name }), _jsx(MenuButton, { type: "button", kind: "pill", onClick: (e) => {
                                                        e.stopPropagation();
                                                        if (openMenu?.sceneId ===
                                                            scene.id) {
                                                            closeMenu();
                                                        }
                                                        else {
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setOpenMenu({
                                                                sceneId: scene.id,
                                                                top: rect.bottom + 4,
                                                                right: rect.right,
                                                            });
                                                        }
                                                    }, children: _jsx(DotDotDotVerticalIcon, {}) })] }), openMenu?.sceneId === scene.id && (_jsx(SceneMenu, { top: openMenu.top, right: openMenu.right, canDelete: scenes.length > 1, onDelete: () => {
                                                SceneService.getNotificationInterface().notifyDeleteScene(scene.id);
                                                closeMenu();
                                            }, onRename: () => {
                                                closeMenu();
                                            }, onClose: closeMenu }))] }, scene.id));
                            }) }), _jsxs(NewSceneButton, { type: "button", kind: "secondary", onClick: () => {
                                SceneService.getNotificationInterface().notifyAddScene();
                            }, children: [_jsx(PlusIcon, {}), "New scene"] })] }) }) }) }));
}
function SceneMenu({ top, right, canDelete, onDelete, onRename, onClose, }) {
    const menuRef = useRef(null);
    useEffect(() => {
        function handleClickOutside(e) {
            if (menuRef.current &&
                !menuRef.current.contains(e.target)) {
                onClose();
            }
        }
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [onClose]);
    return createPortal(_jsxs(MenuDropdown, { ref: menuRef, style: {
            top,
            right: `calc(100vw - ${right}px)`,
        }, children: [_jsxs(MenuItem, { onClick: onRename, children: [_jsx(EditIcon, {}), "Rename"] }), canDelete && (_jsxs(MenuItem, { "$destructive": true, onClick: onDelete, children: [_jsx(TrashCanIcon, {}), "Delete scene"] }))] }), document.body);
}
const ScenesPanelRoot = styled.div `
    height: 100%;
    position: relative;
`;
const Content = styled.div `
    position: absolute;
    top: 0;
    height: 100%;
    left: 1.5rem;
    right: 0;
    overflow: hidden;

    background: rgba(57, 56, 56, 0.6);
    backdrop-filter: blur(50px);
    border: 1.4px solid rgba(255, 255, 255, 0.4);
    border-radius: 16px;
`;
const Inner = styled.div `
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.25rem;
    box-sizing: border-box;
    width: 100%;
`;
const Header = styled.div `
    ${TextStyle.Heading_3};
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;
const Title = styled.div `
    font-weight: 600;
    flex-grow: 1;
    color: white;
`;
const HeaderButton = styled(Button) `
    --Button_Height: 1.75rem;
    --Button_Width: 1.75rem;

    svg {
        transform: scale(0.75);
    }
`;
const SceneList = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;
const SceneCard = styled.div `
    position: relative;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    border-radius: 12px;
    background: #aaa3ff;
    border: 2px solid
        ${({ $isDisplayScene }) => $isDisplayScene
    ? 'rgba(255, 255, 255, 0.95)'
    : 'rgba(255, 255, 255, 0.3)'};
    box-shadow: ${({ $isDisplayScene }) => $isDisplayScene
    ? '0 0 0 2px rgba(170, 163, 255, 0.8), 0 0 12px rgba(170, 163, 255, 0.4)'
    : 'none'};
    backdrop-filter: blur(50px);
    transition:
        border-color 150ms ease,
        box-shadow 150ms ease;
`;
const ThumbnailWrapper = styled.div `
    position: relative;
    width: 100%;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
`;
const SceneThumbnail = styled.div `
    width: 100%;
    aspect-ratio: 16 / 9;
    background: ${({ $bg, $bgImageUrl }) => {
    if ($bgImageUrl)
        return `url(${$bgImageUrl}) center / cover no-repeat`;
    if ($bg)
        return $bg;
    return 'rgba(30, 30, 38, 0.7)';
}};
`;
const SceneCanvas = styled.canvas `
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
`;
const ThumbnailImage = styled.img `
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;
const CardFooter = styled.div `
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0 0.5rem;
    height: 32px;
`;
const LiveBadge = styled.button `
    all: unset;
    position: absolute;
    top: 0.375rem;
    left: 0.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 1.5rem;
    background: ${({ $isLive }) => ($isLive ? '#e53e3e' : 'rgba(120, 120, 120, 0.7)')};
    color: white;
    font-size: 0.625rem;
    font-weight: 700;
    line-height: 1.1;
    border-radius: 0.25rem;
    letter-spacing: 0.05em;
    cursor: pointer;
    text-align: center;
    transition: background 150ms ease;

    &:hover {
        background: ${({ $isLive }) => ($isLive ? '#c53030' : 'rgba(150, 150, 150, 0.8)')};
    }
`;
const SceneLabel = styled.div `
    flex: 1;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 22px;
    color: #1e1e1e;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;
const MenuButton = styled(Button) `
    --Button_Height: 1.5rem;
    --Button_Width: 1.5rem;
    --Button_Background: transparent;
    --Button_HoverBackground: rgba(0, 0, 0, 0.15);
    --Button_BorderRadius: 0.375rem;
    color: rgba(30, 30, 38, 0.6);
    flex-shrink: 0;

    svg {
        transform: scale(0.8);
    }
`;
const MenuDropdown = styled.div `
    position: fixed;
    min-width: 10rem;
    background: rgba(57, 56, 56, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 0.625rem;
    padding: 0.375rem;
    display: flex;
    flex-direction: column;
    z-index: 1000;
`;
const MenuItem = styled.button `
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.625rem;
    border: none;
    background: none;
    color: ${({ $destructive }) => $destructive ? '#FF5F5F' : 'rgba(255, 255, 255, 0.9)'};
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    border-radius: 0.375rem;
    white-space: nowrap;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
    }

    svg {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
    }
`;
const NewSceneButton = styled(Button) `
    --Button_Height: 2.5rem;
    --Button_Background: rgba(255, 255, 255, 0.05);
    --Button_HoverBackground: rgba(255, 255, 255, 0.1);
    --Button_BorderRadius: 0.75rem;
    width: 100%;
    gap: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: white;
`;
